/**
 * Origen de noticias del tenant (feature #118).
 *
 * La configuración vive en `tenants/{tenantId}/settings/news_source`, igual que
 * el resto de settings del tenant (`verifactu`, `season`,
 * `notification_recipients`). NO va inline en `TenantConfig` porque el doc raíz
 * del tenant es de lectura pública y `lastSyncError` no debe serlo.
 *
 * Reglas: lectura para miembros del tenant y super-admin, escritura solo
 * admin/super-admin (regla genérica `settings/{settingId}` de
 * `apps/firebase/firestore.rules`). `news_source` NUNCA debe añadirse a la lista
 * de settings de lectura pública.
 */

/**
 * Qué alimenta el apartado de noticias del tenant.
 * - `manual`   — solo altas manuales desde el admin (comportamiento histórico).
 * - `external` — solo noticias importadas del origen externo.
 * - `hybrid`   — importadas + propias creadas en el admin.
 */
export type NewsSourceMode = 'manual' | 'external' | 'hybrid';

/**
 * Adaptador de origen. En la feature #118 solo se implementa `wordpress`;
 * `rss` queda declarado para no romper el modelo cuando se añada.
 */
export type NewsSourceType = 'wordpress' | 'rss';

/**
 * Resultado de la última sincronización.
 * - `never`   — todavía no se ha ejecutado ninguna.
 * - `ok`      — ventana procesada y barrido de ids completo.
 * - `partial` — importación correcta con degradación conocida (alguna imagen
 *               falló, o el barrido de ids topó con el límite de páginas).
 * - `error`   — no se pudo leer el origen o falló la escritura.
 */
export type NewsSyncStatus = 'never' | 'ok' | 'partial' | 'error';

/** Id fijo del documento de settings del origen de noticias. */
export const NEWS_SOURCE_SETTINGS_ID = 'news_source';

/** Contadores de la última ejecución del sync para un tenant. */
export interface NewsSyncStats {
  /** Noticias creadas en esta ejecución. */
  created: number;
  /** Noticias ya existentes que se han actualizado. */
  updated: number;
  /** Noticias marcadas `active: false` por desaparecer del origen. */
  deactivated: number;
  /** Posts omitidos (no interpretables o degradados). */
  skipped: number;
  /** Noticias procesadas en esta ejecución que quedaron sin portada (R64). */
  withoutCover: number;
  /**
   * Imágenes incrustadas en el cuerpo copiadas a Storage en esta ejecución.
   * Campo **aditivo y opcional**: los documentos escritos antes de la corrección
   * de imágenes incrustadas no lo tienen. No degrada el estado del sync.
   */
  inlineImagesMirrored?: number;
  /**
   * Imágenes incrustadas que se han quedado apuntando al servidor de origen
   * (fallo de descarga, URL no pública o tope por noticia/tiempo alcanzado).
   * Contador informativo: **no** degrada el estado del sync (mismo criterio que
   * `withoutCover`, R63).
   */
  inlineImagesSkipped?: number;
}

/**
 * Documento `tenants/{tenantId}/settings/news_source`.
 * Admin-writable, nunca de lectura pública.
 */
export interface TenantNewsSourceSettings {
  id: typeof NEWS_SOURCE_SETTINGS_ID;
  /** manual = solo alta manual · external = solo WP · hybrid = WP + propias */
  mode: NewsSourceMode;
  /** Adaptador de origen. En esta feature solo se implementa 'wordpress'. */
  type: NewsSourceType;
  /** Raíz del sitio, sin barra final ni sufijo /wp-json. Ej: https://club.example */
  baseUrl: string;
  /** Filtro opcional por categorías de WordPress (ids numéricos). */
  categoryIds?: number[];
  /** Código de idioma para WP multi-idioma (Polylang/WPML). Vacío ⇒ sin filtro. */
  language?: string;
  /** Solo gobierna la ejecución programada; el resync manual no lo consulta. */
  syncEnabled: boolean;
  lastSyncAt?: Date;
  lastSyncStatus?: NewsSyncStatus;
  /** Mensaje legible, recortado a 500 caracteres. */
  lastSyncError?: string;
  lastSyncStats?: NewsSyncStats;
  updatedAt?: Date;
  updatedBy?: string;
}
